import { useEffect } from "react";
import { Link } from "react-router-dom";
import { SiteNav, COMPANY_LOGO } from "@/components/SiteNav";
import { Brain, Code2, LayoutDashboard, RefreshCw, Workflow, ShoppingCart, Smartphone, Users, Lightbulb, Bot, TrendingUp, ArrowRight, Building2 } from "lucide-react";

const FOCUS = [
  [Brain, "Artificial intelligence"], [Code2, "Software & application development"], [LayoutDashboard, "Business management technology"],
  [RefreshCw, "Digital transformation"], [Workflow, "Automation"], [ShoppingCart, "E-commerce solutions"], [Smartphone, "Consumer digital products"],
];

const PRINCIPLES = [
  [Users, "User-focused design", "We start with the people who will use what we build, and shape products around their real tasks."],
  [Lightbulb, "Practical innovation", "We apply new technology where it solves a genuine problem, not for its own sake."],
  [Bot, "Intelligent automation", "We automate repetitive work so people can spend their time on decisions that matter."],
  [TrendingUp, "Continuous improvement", "We listen, measure and refine our products steadily over time."],
];

const PRODUCTS = [
  { title: "Business CRM & dashboards", text: "Manage contacts, pipelines, tasks and reporting in one place.", to: "/crm", cta: "Explore CRM" },
  { title: "Veyronix Commerce", text: "E-commerce consultancy and services, from store builds to growth support.", to: "/commerce", cta: "Explore Commerce" },
  { title: "AI Studio", text: "AI-powered tools for creating and refining business content.", to: "/ai-studio", cta: "Explore AI Studio" },
  { title: "Business-management solutions", text: "EPOS, bookings and online store tools for merchants, with clear plans.", to: "/pricing", cta: "View plans" },
];

const Section = ({ eyebrow, title, children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-6 py-20 ${className}`}>
    <p className="text-xs uppercase tracking-[0.3em] text-indigo-600 mb-3">{eyebrow}</p>
    {title && <h2 className="font-brand text-base md:text-lg font-bold text-slate-900 mb-10 max-w-2xl">{title}</h2>}
    {children}
  </section>
);

const Hero = () => (
  <header className="relative overflow-hidden bg-slate-900 text-white" data-testid="about-hero">
    <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-indigo-600/25 blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32">
      <p className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-5">About Veyronix Limited</p>
      <h1 className="font-brand text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl mb-8" data-testid="about-hero-title">Building Smarter Digital Experiences.</h1>
      <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed" data-testid="about-hero-text">Veyronix Limited is a UK-based technology company developing intelligent software, digital products and practical business solutions. We combine technology, thoughtful design and automation to help businesses operate more efficiently and individuals achieve more.</p>
    </div>
  </header>
);

const WhoWeAre = () => (
  <Section eyebrow="Who we are" title="A UK technology company building software for businesses and individuals.">
    <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-focus-areas">
      {FOCUS.map(([Icon, label]) => (
        <li key={label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 transition-[border-color,transform] duration-200 hover:border-indigo-300 hover:-translate-y-0.5">
          <Icon className="h-5 w-5 text-indigo-600 shrink-0" /><span className="text-sm font-medium text-slate-800">{label}</span>
        </li>
      ))}
    </ul>
  </Section>
);

const MissionVision = () => (
  <section className="bg-slate-900 text-white">
    <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-6">
      {[["Our mission", "To make intelligent technology accessible, useful and intuitive for businesses and individuals.", "about-mission"],
        ["Our vision", "To build an ecosystem of reliable digital products that simplify complex tasks, support better decisions and create meaningful experiences.", "about-vision"]].map(([t, q, id]) => (
        <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10" data-testid={id}>
          <p className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-4">{t}</p>
          <p className="font-brand text-xl md:text-2xl font-semibold leading-snug">“{q}”</p>
        </div>
      ))}
    </div>
  </section>
);

const Principles = () => (
  <Section eyebrow="Our principles" title="How we work">
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {PRINCIPLES.map(([Icon, t, d], i) => (
        <div key={t} className="relative rounded-2xl border border-slate-200 bg-white p-6 overflow-hidden" data-testid={`about-principle-${i + 1}`}>
          <span className="absolute right-4 top-2 font-brand text-6xl font-bold text-slate-100 select-none" aria-hidden="true">0{i + 1}</span>
          <Icon className="relative h-6 w-6 text-indigo-600 mb-4" />
          <h3 className="relative font-brand font-bold text-slate-900 mb-2">{t}</h3>
          <p className="relative text-sm text-slate-600 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </Section>
);

const Products = () => (
  <Section eyebrow="Products & solutions" title="What we offer" className="pt-0">
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {PRODUCTS.map(p => (
        <Link key={p.title} to={p.to} className="group rounded-2xl border border-slate-200 bg-white p-6 transition-[border-color,transform,box-shadow] duration-200 hover:border-indigo-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`about-product-${p.to.slice(1)}`}>
          <h3 className="font-brand font-bold text-slate-900 mb-2">{p.title}</h3>
          <p className="text-sm text-slate-600 mb-5">{p.text}</p>
          <span className="text-sm font-semibold text-indigo-600 inline-flex items-center gap-1">{p.cta}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      ))}
      <div className="rounded-2xl p-6 text-white md:col-span-2 lg:col-span-2 flex flex-col sm:flex-row gap-6 items-start" style={{ background: "linear-gradient(135deg,#071526,#10263D)" }} data-testid="about-product-pulse">
        <img src="/pulse-assets/pulse-icon-512.png" alt="PULSE by Veyronix logo" width="72" height="72" className="rounded-2xl shrink-0" />
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h3 className="font-brand font-bold tracking-wide">PULSE by Veyronix</h3>
            <span className="rounded-full border px-2.5 py-0.5 text-[11px] uppercase tracking-widest" style={{ borderColor: "#19354D", color: "#55E4E7" }}>Coming soon</span>
          </div>
          <p className="text-sm mb-5" style={{ color: "#AFC3D2" }}>A fitness and nutrition companion for tracking meals, workouts, water and weight, with AI-assisted food scanning. Launching in the UK first.</p>
          <Link to="/pulse" className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: "#55E4E7" }} data-testid="about-explore-pulse">Explore PULSE<ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </div>
  </Section>
);

const CompanyDetails = () => (
  <footer className="bg-slate-950 text-slate-400" data-testid="about-company-details">
    <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-10 text-sm">
      <div>
        <div className="flex items-center gap-3 mb-4"><img src={COMPANY_LOGO} alt="" className="h-7 w-auto brightness-0 invert opacity-70" /><span className="font-brand font-bold text-white">VEYRONIX</span></div>
        <p>Building smarter digital experiences.</p>
      </div>
      <div>
        <p className="flex items-center gap-2 text-white font-semibold mb-3"><Building2 className="h-4 w-4" />Company details</p>
        <p>Veyronix Limited</p>
        <p>Registered in England and Wales, company number 16821530</p>
        <p>Registered office: 64B Borthwick Road, London E15 1UE, United Kingdom</p>
      </div>
      <div className="space-y-1.5">
        <p className="text-white font-semibold mb-3">Links</p>
        <a href="https://veyronix.co.uk" className="block hover:text-white">veyronix.co.uk</a>
        <Link to="/pulse" className="block hover:text-white">PULSE by Veyronix</Link>
        <Link to="/privacy" className="block hover:text-white">Privacy Policy</Link>
        <Link to="/terms" className="block hover:text-white">Terms</Link>
      </div>
    </div>
    <p className="text-center text-xs text-slate-600 pb-8">© {new Date().getFullYear()} Veyronix Limited</p>
  </footer>
);

export default function AboutPage() {
  useEffect(() => { document.title = "About Us | Veyronix Limited"; }, []);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <SiteNav active="about" />
      <Hero />
      <WhoWeAre />
      <MissionVision />
      <Principles />
      <Products />
      <CompanyDetails />
    </div>
  );
}
